import type { DomainConfig } from '@creator/sdk/modules/config/config.model';
import i18n from 'i18next';
import { Fragment, useEffect, useState } from 'react';
import { initReactI18next } from 'react-i18next';
import { BrowserRouter as Router } from 'react-router';
import ScrollToTop from './basic-components/ScrollToTop/ScrollToTop';
import AppsFlyerBanner from './components/AppsFlyerBanner/AppsFlyerBanner';
import OneTapOnly from './components/Auth/OneTapOnly/OneTapOnly';
import ComscoreTracker from './components/ComscoreTracker/ComscoreTracker';
import NavigationTracker from './components/NavigationTracker/NavigationTracker';
import SystemBackdoor from './components/SystemBackdoor/SystemBackdoor';
import { getDomainName } from './config';
import { ModalProvider } from './context/modal-context';
import { loadLanguage } from './model/config/actions';
import { useStoreActions, useStoreState } from './model/hooks';
import { processSSRData } from './model/ssr-data/ssr-data';
import { getUserId } from './model/user/helpers';
import { initializeGTM } from './services/gtmService';
import storageService from './services/storage-service';
import { preloadCriticalRoutes } from './utils/route-preloader';
import './styles.css';
import PlaywireRamp from './components/Ads/PlaywireRamp';
import AppRoutes from './Routes';
import { getQueryParam, removeQueryParamFromUrl } from './utils/url-utils/url-utils';

export default function App() {
    const domainName = getDomainName();

    const authenticateOnStartup = useStoreActions(actions => actions.user.authenticateOnStartup);
    const loadDomaineConfig = useStoreActions(actions => actions.config.loadDomainConfig);
    const loadDomainMainMenu = useStoreActions(actions => actions.config.loadDomainMainMenu);
    const loadUser = useStoreActions(actions => actions.user.loadUser);
    const loadRTBPrice = useStoreActions(actions => actions.tokenPrice.loadRTBPrice);
    const loadMyUserInfo = useStoreActions(actions => actions.user.loadMyUserInfo);
    const loadDomainPublicData = useStoreActions(actions => actions.domain.loadPublicData);
    const loadCommunityDefaults = useStoreActions(actions => actions.publicSettings.loadCommunityDefaults);
    const loadDomainsSymbols = useStoreActions(actions => actions.publicSettings.loadDomainsSymbols);
    const loadExternalArticleDomain = useStoreActions(actions => actions.publicSettings.loadExternalArticleDomain);
    const isLoggedIn = useStoreState(state => state.user.isLoggedIn);
    const isAuthenticating = useStoreState(state => state.user.isAuthenticating);

    const refBy = getQueryParam('refBy') || '';
    const refFrom = getQueryParam('refFrom') || '';

    const [isAppReady, setIsAppReady] = useState(false);

    useEffect(() => {
        console.time('isAppReady');
        processSSRData();
        initDomainConfig();
        loadDomainMainMenu();
        // injectDomainCustomCss();
        loadDomainsSymbols();
        loadExternalArticleDomain();
        loadCommunityDefaults();
        authenticateOnStartup();
        loadDomainPublicData(domainName);
        loadRTBPrice();
        if (refBy || refFrom) setRefByAndForm();

        // Preload critical routes for better performance
        preloadCriticalRoutes();
    }, []);

    useEffect(() => {
        if (!isAppReady) return;
        // remove loading
        document.documentElement.classList.remove('loading');
        console.timeEnd('isAppReady');
    }, [isAppReady]);

    useEffect(() => {
        if (!isLoggedIn) return;

        const userId = getUserId();
        loadUser(userId);
        loadMyUserInfo();
    }, [isLoggedIn]);

    useEffect(() => {
        if (isAuthenticating !== false) return; // false means we done, we are after operations
        if (refBy || refFrom) setRefByAndForm();
    }, [isAuthenticating]);

    async function initDomainConfig() {
        const [domainConfig] = await Promise.all([loadDomaineConfig(), initI18n()]);
        init(domainConfig);
    }

    function setRefByAndForm(): void {
        if (refBy) storageService.localStorage.set('refBy', refBy);
        if (refFrom) storageService.localStorage.set('refFrom', refFrom);

        removeQueryParamFromUrl('refBy');
        removeQueryParamFromUrl('refFrom');
    }

    async function init(domainConfig: DomainConfig) {
        if (domainConfig.googleTagManager) initializeGTM(domainConfig.googleTagManager.id, domainConfig.googleTagManager.enableDebug);

        setIsAppReady(true);
    }

    async function initI18n() {
        const lang = 'en';
        const langResource = await loadLanguage(lang);
        const i18nConfig = {
            fallbackLng: 'en',
            debug: false,
            lng: lang,
            resources: {
                [lang]: {
                    translation: langResource,
                },
            },
            interpolation: {
                escapeValue: false, // React already escapes by default
            },
        };
        await i18n.use(initReactI18next).init(i18nConfig);
    }

    if (!isAppReady) return <div />; // TODO - use <Suspend />

    return (
        <Fragment>
            <Router basename="/">
                <ModalProvider>
                    <ScrollToTop />
                    <NavigationTracker />
                    <ComscoreTracker />
                    <OneTapOnly />
                    <SystemBackdoor isAppReady={isAppReady} />
                    <AppRoutes />
                    <PlaywireRamp />
                </ModalProvider>
            </Router>
            <div id="modal-root" />
        </Fragment>
    );
}
